import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { FeatureFlagService } from '@galaxy/partner';
import { LineItem, SalesOrdersService } from '@vendasta/sales-orders';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class UnifiedOrderActivationsGuard {
  constructor(
    private readonly router: Router,
    private readonly featureService: FeatureFlagService,
    private readonly salesOrdersService: SalesOrdersService,
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    @Inject('MARKET_ID') private readonly marketId$: Observable<string>,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const replacePCactivationsFeatureFlag = 'replace_pc_activations_workflow';
    const accountGroupId = route?.params?.accountGroupId;
    const lineItems = this.lineItemsFromRoute(route);

    const replacePCactivationsFeatureFlagEnabled$ = combineLatest([this.partnerId$, this.marketId$]).pipe(
      switchMap(([partnerId, marketId]) =>
        this.featureService.batchGetStatus(partnerId, marketId, [replacePCactivationsFeatureFlag]),
      ),
      map((flags) => {
        return !!flags[replacePCactivationsFeatureFlag];
      }),
    );

    return combineLatest([replacePCactivationsFeatureFlagEnabled$, this.partnerId$, this.marketId$]).pipe(
      switchMap(([replacePCactivationsFeatureFlagEnabled, partnerId, marketId]) => {
        if (replacePCactivationsFeatureFlagEnabled) {
          return this.salesOrdersService.createDraft({
            partnerId: partnerId,
            marketId: marketId,
            businessId: accountGroupId,
            requestedActivation: new Date(),
            lineItems: lineItems,
          });
        }
        return of(null);
      }),
      map((orderId) => {
        if (orderId) {
          return this.router.createUrlTree([`/order-management/${accountGroupId}/view/${orderId}`]);
        }
        return true;
      }),
    );
  }

  lineItemsFromRoute(route: ActivatedRouteSnapshot): LineItem[] {
    const appJson = route?.queryParams?.appIds;
    let apps = null;
    if (appJson) {
      apps = JSON.parse(appJson);
    }

    const addonJson = route?.queryParams?.addonIds;
    let addons = null;
    if (addonJson) {
      addons = JSON.parse(addonJson);
    }

    const lineItems: LineItem[] = [];

    if (apps) {
      for (const app of apps) {
        lineItems.push(
          new LineItem({
            appKey: {
              appId: app.appId,
              editionId: app.editionId || '',
            },
            quantity: app.quantity || 1,
            currentRevenue: {
              revenueComponents: [{}],
            },
          }),
        );
      }
    }

    if (addons) {
      for (const addon of addons) {
        lineItems.push(
          new LineItem({
            appKey: {
              appId: addon.addonId,
            },
            quantity: addon.quantity || 1,
            currentRevenue: {
              revenueComponents: [{}],
            },
          }),
        );
      }
    }

    return lineItems;
  }
}
